import { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import {
  Upload, ScanLine, Sparkles, Loader2, Car, ArrowLeft, ImageIcon,
  Plus, X, Wrench, Minus, ListPlus, Camera, Tag, Store, Truck,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";

const FIELD_KEYS = ["vin", "brand", "model", "year", "plate", "fuel", "engine", "color"];
const FIELD_TKEY = { vin: "fVin", brand: "fBrand", model: "fModel", year: "fYear", plate: "fPlate", fuel: "fFuel", engine: "fEngine", color: "fColor" };

const SUGGESTIONS = [
  "Plaquettes de frein avant", "Plaquettes de frein arrière", "Disques de frein",
  "Filtre à huile", "Filtre à air", "Filtre habitacle", "Batterie",
  "Alternateur", "Démarreur", "Kit de distribution", "Pompe à eau",
  "Amortisseurs avant", "Embrayage", "Bougies d'allumage", "Rétroviseur",
  "Phare avant", "Essuie-glaces", "Courroie d'accessoire",
];

export default function NewRequest() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const partFileRef = useRef(null);
  const photoIdxRef = useRef(null);
  const [vehicle, setVehicle] = useState({ vin: "", brand: "", model: "", year: "", plate: "", fuel: "", engine: "", color: "" });
  const [image, setImage] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState("");
  const [delivery, setDelivery] = useState({ method: "pickup", address: "", city: "" });
  const [submitting, setSubmitting] = useState(false);

  const setField = (k) => (e) => setVehicle({ ...vehicle, [k]: e.target.value });

  const onFile = async (file) => {
    if (!file) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("nr.tUnsupported"));
      return;
    }
    const reader = new FileReader();
    reader.onload = async () => { setImage(reader.result); await analyze(reader.result); };
    reader.readAsDataURL(file);
  };

  const analyze = async (dataUrl) => {
    setAnalyzing(true);
    try {
      const { data } = await api.post("/vehicle/analyze", { image: dataUrl });
      const v = data.vehicle || {};
      setVehicle((prev) => ({ ...prev, ...Object.fromEntries(Object.entries(v).filter(([, val]) => val)) }));
      toast.success(t("nr.tAnalyzed"));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("nr.tAnalyzeFail"));
    } finally { setAnalyzing(false); }
  };

  const addItem = (name) => {
    const n = (name || "").trim();
    if (!n) return;
    const exists = items.find((i) => i.name.toLowerCase() === n.toLowerCase());
    if (exists) {
      setItems(items.map((i) => (i === exists ? { ...i, quantity: i.quantity + 1 } : i)));
    } else {
      setItems([...items, { name: n, quantity: 1, note: "", reference: "", photo: null, aiIdentify: false, identifying: false }]);
    }
    setDraft("");
  };

  const addPhotoPart = () => {
    const newIdx = items.length;
    setItems([...items, { name: t("nr.toIdentify"), quantity: 1, note: "", reference: "", photo: null, aiIdentify: true, identifying: false }]);
    photoIdxRef.current = newIdx;
    setTimeout(() => partFileRef.current?.click(), 50);
  };

  const openPartPhoto = (idx) => {
    photoIdxRef.current = idx;
    partFileRef.current?.click();
  };

  const identifyPart = async (idx, photoOverride) => {
    const photo = photoOverride || items[idx]?.photo;
    if (!photo) { toast.error(t("nr.tAddPhotoFirst")); return; }
    setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, identifying: true } : x)));
    try {
      const { data } = await api.post("/part/identify", { image: photo, vehicle });
      setItems((prev) => prev.map((x, i) => {
        if (i !== idx) return x;
        const patch = { identifying: false };
        if (data.name) patch.name = data.name;
        if (data.reference && !x.reference) patch.reference = data.reference;
        return { ...x, ...patch };
      }));
      if (data.name) {
        const pct = Math.round((data.confidence || 0) * 100);
        toast.success(t("nr.tIdentified", { name: data.name }) + (pct ? t("nr.tReliability", { pct }) : ""));
      } else {
        toast.error(t("nr.tNotRecognized"));
      }
    } catch (err) {
      setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, identifying: false } : x)));
      toast.error(formatApiError(err.response?.data?.detail) || t("nr.tIdFail"));
    }
  };

  const toggleAI = (idx, checked) => {
    setItems((prev) => prev.map((x, i) => (i === idx ? { ...x, aiIdentify: checked } : x)));
    if (checked) {
      if (items[idx]?.photo) identifyPart(idx);
      else toast.info(t("nr.tAddPhotoInfo"));
    }
  };

  const onPartPhoto = (file) => {
    const idx = photoIdxRef.current;
    if (!file || idx == null) return;
    if (!["image/jpeg", "image/png", "image/webp"].includes(file.type)) {
      toast.error(t("nr.tUnsupported"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setItems((prev) => prev.map((it, i) => (i === idx ? { ...it, photo: reader.result } : it)));
      const wantsAI = items[idx]?.aiIdentify;
      if (wantsAI) identifyPart(idx, reader.result);
      else toast.success(t("nr.tPhotoAdded"));
    };
    reader.readAsDataURL(file);
  };

  const updateItem = (idx, patch) => setItems(items.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));
  const setQty = (idx, delta) => setItems(items.map((it, i) => (i === idx ? { ...it, quantity: Math.max(1, it.quantity + delta) } : it)));

  const onDraftKey = (e) => { if (e.key === "Enter") { e.preventDefault(); addItem(draft); } };

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) { toast.error(t("nr.tAddOnePart")); return; }
    if (delivery.method === "delivery" && !delivery.address.trim()) {
      toast.error(t("nr.tAddrRequired")); return;
    }
    setSubmitting(true);
    try {
      const cleanParts = items.map(({ name, quantity, note, reference, photo }) => ({ name, quantity, note, reference, photo }));
      const { data } = await api.post("/quotes", { parts: cleanParts, vehicle, carte_grise_image: image, delivery });
      toast.success(t("nr.tSent"));
      navigate(`/app/demande/${data.id}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || t("nr.tSendError"));
    } finally { setSubmitting(false); }
  };

  const availableSuggestions = SUGGESTIONS.filter((s) => !items.some((i) => i.name.toLowerCase() === s.toLowerCase()));

  return (
    <div>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-800" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> {t("nr.back")}
      </button>
      <h1 className="font-display text-3xl font-bold tracking-tight">{t("nr.title")}</h1>
      <p className="mt-1.5 text-sm text-slate-500">{t("nr.sub")}</p>

      <form onSubmit={submit} className="mt-8 space-y-6">
        {/* AI upload */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold">{t("nr.aiTitle")}</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">{t("nr.aiSub")}</p>

          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
            onChange={(e) => onFile(e.target.files[0])} data-testid="carte-grise-file-input" />

          <button type="button" onClick={() => fileRef.current?.click()} disabled={analyzing}
            data-testid="upload-carte-grise-btn"
            className="mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-blue-200 bg-blue-50/50 px-6 py-8 text-center transition-colors hover:border-blue-400 hover:bg-blue-50 disabled:opacity-60">
            {analyzing ? (
              <><Loader2 className="h-7 w-7 animate-spin text-blue-600" /><span className="text-sm font-medium text-blue-700">{t("nr.analyzing")}</span></>
            ) : image ? (
              <><ImageIcon className="h-7 w-7 text-blue-600" /><span className="text-sm font-medium text-blue-700">{t("nr.photoLoaded")}</span></>
            ) : (
              <><Upload className="h-7 w-7 text-blue-600" /><span className="text-sm font-medium text-blue-700">{t("nr.dropChoose")}</span></>
            )}
          </button>

          {image && (
            <div className="mt-4 flex items-center gap-3">
              <img src={image} alt="carte grise" className="h-20 w-28 rounded-lg border border-slate-200 object-cover" />
              <span className="text-xs text-slate-400">{t("nr.preview")}</span>
            </div>
          )}
        </div>

        {/* Vehicle fields */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Car className="h-5 w-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold">{t("nr.vehTitle")}</h2>
            <span className="ml-auto flex items-center gap-1 text-xs text-slate-400"><ScanLine className="h-3.5 w-3.5" /> {t("nr.aiOrManual")}</span>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {FIELD_KEYS.map((k) => (
              <div key={k} className={k === "vin" ? "sm:col-span-2" : ""}>
                <Label htmlFor={k}>{t(`nr.${FIELD_TKEY[k]}`)}</Label>
                <Input id={k} data-testid={`vehicle-${k}-input`} value={vehicle[k]} onChange={setField(k)}
                  className={`mt-1.5 ${k === "vin" ? "font-mono" : ""}`}
                  placeholder={k === "vin" ? t("nr.vinPh") : ""} />
              </div>
            ))}
          </div>
        </div>

        {/* Parts builder */}
        <div className="rounded-xl border border-slate-200 bg-white p-6">
          <div className="flex items-center gap-2">
            <Wrench className="h-5 w-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold">{t("nr.partsTitle")}</h2>
            {items.length > 0 && <span className="ml-auto rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold text-blue-700">{items.length} {items.length > 1 ? t("common.pieces") : t("common.piece")}</span>}
          </div>
          <p className="mt-1 text-sm text-slate-500">{t("nr.partsSub")}</p>

          {/* Add input */}
          <input ref={partFileRef} type="file" accept="image/jpeg,image/png,image/webp" hidden
            onChange={(e) => { onPartPhoto(e.target.files[0]); e.target.value = ""; }} data-testid="part-photo-file-input" />

          <div className="mt-4 flex flex-col gap-2 sm:flex-row">
            <Input
              data-testid="part-name-input"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onDraftKey}
              placeholder={t("nr.addPh")}
            />
            <div className="flex gap-2">
              <Button type="button" onClick={() => addItem(draft)} data-testid="add-part-btn" className="shrink-0 bg-blue-600 hover:bg-blue-700">
                <Plus className="mr-1 h-4 w-4" /> {t("nr.add")}
              </Button>
              <Button type="button" variant="outline" onClick={addPhotoPart} data-testid="add-part-photo-btn" className="shrink-0 border-slate-300">
                <Camera className="mr-1 h-4 w-4" /> {t("nr.byPhoto")}
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-slate-400">{t("nr.proHint")}</p>

          {/* Suggestions */}
          {availableSuggestions.length > 0 && (
            <div className="mt-4">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide text-slate-400">
                <ListPlus className="h-3.5 w-3.5" /> {t("nr.quickSugg")}
              </p>
              <div className="flex flex-wrap gap-2">
                {availableSuggestions.slice(0, 10).map((s) => (
                  <button key={s} type="button" onClick={() => addItem(s)} data-testid={`suggestion-${s}`}
                    className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1.5 text-xs font-medium text-slate-600 transition-colors hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700">
                    + {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Items list */}
          {items.length === 0 ? (
            <div className="mt-5 rounded-lg border border-dashed border-slate-200 py-8 text-center text-sm text-slate-400">
              {t("nr.emptyParts")}
            </div>
          ) : (
            <div className="mt-5 space-y-3" data-testid="parts-list">
              {items.map((it, idx) => (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50/60 p-3.5" data-testid={`part-item-${idx}`}>
                  <div className="flex items-center gap-3">
                    <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-white text-blue-600 shadow-sm"><Wrench className="h-4 w-4" /></span>
                    <p className="flex-1 font-medium text-slate-800">{it.name}</p>
                    <div className="flex items-center rounded-lg border border-slate-200 bg-white">
                      <button type="button" onClick={() => setQty(idx, -1)} data-testid={`part-qty-minus-${idx}`} className="grid h-8 w-8 place-items-center text-slate-500 hover:text-blue-600"><Minus className="h-3.5 w-3.5" /></button>
                      <span className="w-7 text-center text-sm font-semibold" data-testid={`part-qty-${idx}`}>{it.quantity}</span>
                      <button type="button" onClick={() => setQty(idx, 1)} data-testid={`part-qty-plus-${idx}`} className="grid h-8 w-8 place-items-center text-slate-500 hover:text-blue-600"><Plus className="h-3.5 w-3.5" /></button>
                    </div>
                    <button type="button" onClick={() => removeItem(idx)} data-testid={`remove-part-${idx}`} className="grid h-8 w-8 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600"><X className="h-4 w-4" /></button>
                  </div>

                  <Input
                    value={it.note}
                    onChange={(e) => updateItem(idx, { note: e.target.value })}
                    data-testid={`part-note-${idx}`}
                    placeholder={t("nr.notePh")}
                    className="mt-2.5 border-slate-200 bg-white text-sm"
                  />

                  {/* Pro reference + photo */}
                  <div className="mt-2.5 flex flex-col gap-2 sm:flex-row sm:items-center">
                    <div className="relative flex-1">
                      <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 rounded bg-slate-900 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white">Pro</span>
                      <Input
                        value={it.reference}
                        onChange={(e) => updateItem(idx, { reference: e.target.value })}
                        data-testid={`part-ref-${idx}`}
                        placeholder={t("nr.refPh")}
                        className="border-slate-200 bg-white pl-12 font-mono text-sm"
                      />
                    </div>
                    {it.photo ? (
                      <div className="flex items-center gap-2">
                        <img src={it.photo} alt="pièce" className="h-11 w-14 rounded-lg border border-slate-200 object-cover" data-testid={`part-photo-preview-${idx}`} />
                        <button type="button" onClick={() => openPartPhoto(idx)} data-testid={`part-photo-change-${idx}`} className="text-xs font-medium text-blue-600 hover:underline">{t("nr.change")}</button>
                        <button type="button" onClick={() => updateItem(idx, { photo: null })} className="text-slate-300 hover:text-rose-600"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <Button type="button" variant="outline" size="sm" onClick={() => openPartPhoto(idx)} data-testid={`part-photo-btn-${idx}`} className="shrink-0 border-slate-300 text-slate-600">
                        <Camera className="mr-1.5 h-4 w-4" /> {t("nr.partPhoto")}
                      </Button>
                    )}
                  </div>

                  {/* AI part recognition */}
                  <label className="mt-2.5 flex cursor-pointer items-center gap-2 text-sm text-slate-600">
                    <Checkbox checked={it.aiIdentify} onCheckedChange={(c) => toggleAI(idx, !!c)} data-testid={`part-ai-${idx}`} disabled={it.identifying} />
                    <span className="flex items-center gap-1.5">
                      <Sparkles className="h-3.5 w-3.5 text-blue-600" />
                      {t("nr.aiReco")}
                      {it.identifying && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />}
                    </span>
                  </label>
                  {it.identifying && <p className="mt-1 pl-6 text-xs text-blue-600">{t("nr.aiAnalyzing")}</p>}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Delivery / pickup choice */}
        <div className="rounded-xl border border-slate-200 bg-white p-6" data-testid="delivery-card">
          <div className="flex items-center gap-2">
            <Truck className="h-5 w-5 text-blue-600" />
            <h2 className="font-display text-lg font-semibold">{t("nr.delivTitle")}</h2>
          </div>
          <p className="mt-1 text-sm text-slate-500">{t("nr.delivSub")}</p>
          <div className="mt-4 grid gap-3 sm:grid-cols-2">
            {[
              { key: "pickup", label: t("nr.pickup"), desc: t("nr.pickupDesc"), Icon: Store },
              { key: "delivery", label: t("nr.delivery"), desc: t("nr.delivDesc"), Icon: Truck },
            ].map(({ key, label, desc, Icon }) => {
              const active = delivery.method === key;
              return (
                <button key={key} type="button" onClick={() => setDelivery({ ...delivery, method: key })}
                  data-testid={`delivery-${key}-btn`}
                  className={`flex items-start gap-3 rounded-xl border-2 p-4 text-left transition-colors ${active ? "border-blue-600 bg-blue-50" : "border-slate-200 hover:border-blue-300 hover:bg-slate-50"}`}>
                  <span className={`grid h-9 w-9 shrink-0 place-items-center rounded-lg ${active ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}><Icon className="h-4 w-4" /></span>
                  <span>
                    <span className={`block text-sm font-semibold ${active ? "text-blue-800" : "text-slate-800"}`}>{label}</span>
                    <span className="block text-xs text-slate-500">{desc}</span>
                  </span>
                </button>
              );
            })}
          </div>
          {delivery.method === "delivery" && (
            <div className="mt-4 grid gap-4 sm:grid-cols-3" data-testid="delivery-address-fields">
              <div className="sm:col-span-2">
                <Label htmlFor="del-address">{t("nr.addr")}</Label>
                <Input id="del-address" data-testid="delivery-address-input" value={delivery.address}
                  onChange={(e) => setDelivery({ ...delivery, address: e.target.value })}
                  className="mt-1.5" placeholder={t("nr.addrPh")} />
              </div>
              <div>
                <Label htmlFor="del-city">{t("nr.city")}</Label>
                <Input id="del-city" data-testid="delivery-city-input" value={delivery.city}
                  onChange={(e) => setDelivery({ ...delivery, city: e.target.value })}
                  className="mt-1.5" placeholder={t("nr.cityPh")} />
              </div>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => navigate("/app")}>{t("nr.cancel")}</Button>
          <Button type="submit" disabled={submitting || items.length === 0} data-testid="submit-quote-btn" className="bg-blue-600 hover:bg-blue-700">
            {submitting ? t("nr.sending") : `${t("nr.send")}${items.length ? ` (${items.length})` : ""}`}
          </Button>
        </div>
      </form>
    </div>
  );
}
